import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  updateProfile,
  signOut,
  type UserCredential,
} from 'firebase/auth';
import type { Locale } from '@easymoving/types';
import { firebaseAuth } from './firebase/init';
import { ensureUserProfile } from './data/users';

/** Stable error codes mapped to i18n keys (auth.errors.*) by the UI. */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'email_in_use'
  | 'weak_password'
  | 'invalid_email'
  | 'popup_closed'
  | 'network'
  | 'unknown';

export class AuthError extends Error {
  code: AuthErrorCode;
  constructor(code: AuthErrorCode) {
    super(code);
    this.name = 'AuthError';
    this.code = code;
  }
}

/** Map a Firebase auth error to one of our stable codes. */
export function mapFirebaseAuthError(err: unknown): AuthError {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return new AuthError('invalid_credentials');
    case 'auth/email-already-in-use':
      return new AuthError('email_in_use');
    case 'auth/weak-password':
      return new AuthError('weak_password');
    case 'auth/invalid-email':
      return new AuthError('invalid_email');
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return new AuthError('popup_closed');
    case 'auth/network-request-failed':
      return new AuthError('network');
    default:
      return new AuthError('unknown');
  }
}

export async function signUpWithEmail(input: {
  email: string;
  password: string;
  name?: string;
  locale: Locale;
}): Promise<void> {
  try {
    const cred = await createUserWithEmailAndPassword(
      firebaseAuth(),
      input.email,
      input.password,
    );
    if (input.name) {
      await updateProfile(cred.user, { displayName: input.name });
    }
    await ensureUserProfile({
      uid: cred.user.uid,
      email: cred.user.email,
      name: input.name ?? cred.user.displayName,
      authProvider: 'password',
      locale: input.locale,
    });
  } catch (err) {
    throw mapFirebaseAuthError(err);
  }
}

export async function signInWithEmail(input: {
  email: string;
  password: string;
}): Promise<void> {
  try {
    await signInWithEmailAndPassword(firebaseAuth(), input.email, input.password);
  } catch (err) {
    throw mapFirebaseAuthError(err);
  }
}

export async function signInWithGoogle(locale: Locale): Promise<void> {
  try {
    const provider = new GoogleAuthProvider();
    const cred: UserCredential = await signInWithPopup(firebaseAuth(), provider);
    if (getAdditionalUserInfo(cred)?.isNewUser) {
      await ensureUserProfile({
        uid: cred.user.uid,
        email: cred.user.email,
        name: cred.user.displayName,
        authProvider: 'google',
        locale,
      });
    }
  } catch (err) {
    throw mapFirebaseAuthError(err);
  }
}

export function signOutUser(): Promise<void> {
  return signOut(firebaseAuth());
}
